<div class="public-roi-container min-h-screen py-5 px-4 md:px-6">
  <p-toast></p-toast>

  <!-- VIEW 1.5: Loading Screen Overlay -->
  <div *ngIf="isLoading" class="roi-loading-overlay">
    <div class="loader-content flex flex-column align-items-center justify-content-center">
      <div class="custom-dots-loader">
        <div class="loader-dot"></div>
        <div class="loader-dot"></div>
        <div class="loader-dot"></div>
        <div class="loader-dot"></div>
        <div class="loader-dot"></div>
        <div class="loader-dot"></div>
        <div class="loader-dot"></div>
        <div class="loader-dot"></div>
      </div>
      <p class="loading-text">Please wait while we calculate your ROI results</p>
    </div>
  </div>

  <!-- Header Title -->
  <div class="form-header-container text-center mb-6">
    <h1 class="calculator-title m-0">GradPilots ROI Calculator</h1>
    <div class="accent-underline"></div>
  </div>

  <!-- Main Grid (Side-by-side) -->
  <div class="flex flex-column lg:flex-row max-w-7xl mx-auto align-items-start gap-4 w-full">
    
    <!-- Left Column: Form -->
    <div class="w-full lg:flex-1">
      <div class="form-card p-4 md:p-5 relative">
        <form [formGroup]="roiForm" (ngSubmit)="calculateRoi()" class="flex flex-column gap-5" autocomplete="off">
          
          <!-- STEP 1: Your Program Details -->
          <div class="step-card">
            <div class="step-header">
              <span class="step-number">1</span>
              <h2 class="step-title">Your Program Details</h2>
            </div>
            
            <div class="step-content">
              <div class="field mb-4">
                <label class="label-heading">WHAT LEVEL OF STUDY?</label>
                <div class="segmented-control">
                  <button type="button" class="segmented-btn" 
                          [class.active]="roiForm.get('level')?.value === 'UG'" 
                          (click)="roiForm.get('level')?.setValue('UG')">
                    Undergraduate
                  </button>
                  <button type="button" class="segmented-btn" 
                          [class.active]="roiForm.get('level')?.value === 'PG'" 
                          (click)="roiForm.get('level')?.setValue('PG')">
                    Postgraduate
                  </button>
                </div>
              </div>

              <div class="form-grid">
                <div class="field">
                  <label for="planToStart" class="label-heading">WHEN DO YOU PLAN TO START?</label>
                  <p-dropdown id="planToStart" [options]="startYears" formControlName="planToStart" optionLabel="name" optionValue="code" placeholder="Select start year" styleClass="w-full" appendTo="body"></p-dropdown>
                </div>

                <div class="field">
                  <label for="university" class="label-heading">WHICH UNIVERSITY?</label>
                  <p-autoComplete 
                    id="university" 
                    formControlName="university" 
                    [suggestions]="universitySearchSuggestions" 
                    (completeMethod)="searchUniversities($event)" 
                    field="name" 
                    placeholder="Search university (e.g. LSE)..."
                    styleClass="w-full"
                    [dropdown]="true"
                    forceSelection="true"
                    [disabled]="!roiForm.get('level')?.value">
                  </p-autoComplete>
                  <small *ngIf="!roiForm.get('level')?.value" class="text-xs text-500 italic mt-1 block">Please select level of study first.</small>
                </div>
              </div>

              <div class="form-grid" *ngIf="roiForm.get('level')?.value === 'PG'">
                <div class="field">
                  <label for="school" class="label-heading">WHICH SCHOOL?</label>
                  <p-dropdown id="school" [options]="schoolsList" formControlName="school" optionLabel="name" placeholder="Select school" styleClass="w-full" [disabled]="isLoadingCourses || schoolsList.length === 0" appendTo="body"></p-dropdown>
                  <span *ngIf="isLoadingCourses" class="text-xs text-500 italic mt-1 block"><i class="pi pi-spin pi-spinner mr-1"></i> Loading schools...</span>
                </div>

                <div class="field" *ngIf="roiForm.get('school')?.value">
                  <label for="course" class="label-heading">WHICH COURSE?</label>
                  <p-dropdown id="course" [options]="coursesList" formControlName="course" optionLabel="name" placeholder="Select course" styleClass="w-full" [disabled]="coursesList.length === 0" appendTo="body"></p-dropdown>
                </div>
              </div>
            </div>
          </div>

          <!-- STEP 2: Your Financial Situation -->
          <div class="step-card">
            <div class="step-header">
              <span class="step-number">2</span>
              <h2 class="step-title">Your Financial Situation</h2>
            </div>

            <div class="step-content">
              <div class="field">
                <label class="label-heading">DO YOU HAVE ANY SCHOLARSHIPS SECURED?</label>
                <div class="scholarship-toggle flex gap-3">
                  <button type="button" class="toggle-btn" 
                          [class.active]="hasScholarship" 
                          (click)="toggleScholarship(true)">
                    Yes
                  </button>
                  <button type="button" class="toggle-btn" 
                          [class.active]="!hasScholarship" 
                          (click)="toggleScholarship(false)">
                    No
                  </button>
                </div>
              </div>

              <div class="field" *ngIf="hasScholarship">
                <label for="scholarshipAmount" class="label-heading">SCHOLARSHIP AMOUNT</label>
                <div class="p-inputgroup">
                  <span class="p-inputgroup-addon">{{ currencySymbol }}</span>
                  <input pInputText id="scholarshipAmount" type="number" formControlName="scholarshipAmount" placeholder="10,000" class="w-full">
                </div>
              </div>

              <div class="field">
                <label class="label-heading">LIVING:</label>
                <div class="living-segmented">
                  <div class="living-option" [class.selected]="roiForm.get('livingSituation')?.value === 'On-campus'" (click)="roiForm.get('livingSituation')?.setValue('On-campus')">
                    <span class="radio-dot"></span> On-campus
                  </div>
                  <div class="living-option" [class.selected]="roiForm.get('livingSituation')?.value === 'Off-campus'" (click)="roiForm.get('livingSituation')?.setValue('Off-campus')">
                    <span class="radio-dot"></span> Off-campus
                  </div>
                  <div class="living-option" [class.selected]="roiForm.get('livingSituation')?.value === 'Living with relatives'" (click)="roiForm.get('livingSituation')?.setValue('Living with relatives')">
                    <span class="radio-dot"></span> Living with relatives (free)
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recommendation Checkbox Banner -->
          <div class="loan-banner">
            <p-checkbox formControlName="loanRecommendation" [binary]="true" id="loanRec" class="mr-2"></p-checkbox>
            <label for="loanRec" class="cursor-pointer font-semibold text-800 line-height-3">
              I would also like a <span class="accent-bold-text">personalised loan recommendation</span> based on my profile.
            </label>
          </div>

          <!-- Submit Button -->
          <button 
            type="submit" 
            class="btn-calculate-roi w-full"
            [disabled]="isLoading">
            <i class="pi pi-calculator mr-2" *ngIf="!isLoading"></i>
            <i class="pi pi-spin pi-spinner mr-2" *ngIf="isLoading"></i>
            <span>Calculate My ROI</span>
          </button>
        </form>
      </div>
    </div>

    <!-- Right Column: Results/Dashboard -->
    <div class="w-full lg:flex-1 relative">
      <div class="results-dashboard flex flex-column gap-4 w-full">
        
        <!-- KPI Cards Grid (2x2) -->
        <div class="grid g-3 w-full m-0 p-0">
          
          <!-- Card 1: Break-Even (NOT blurred) -->
          <div class="col-6 p-2" [class.unlocked-card]="calculationResult">
            <div class="kpi-card flex flex-column justify-content-between">
              <span class="kpi-label uppercase">Break-Even</span>
              <span class="kpi-value font-extrabold">{{ calculationResult ? (calculationResult.returns.breakEvenYears + ' Years') : '—' }}</span>
              <span class="kpi-label lowercase text-xs" style="color: #ffffff !important; opacity: 0.8; font-weight: 500;">from graduation</span>
              <svg class="kpi-wave" viewBox="0 0 300 135" preserveAspectRatio="none">
                <path d="M 0 90 C 80 135, 180 120, 230 95 C 270 75, 290 65, 300 55 L 300 135 L 0 135 Z" fill="#1322b7"/>
              </svg>
            </div>
          </div>

          <!-- Card 2: Net Investment (blurred when calculated) -->
          <div class="col-6 p-2" [class.blurred-content]="calculationResult">
            <div class="kpi-card flex flex-column justify-content-between">
              <span class="kpi-label uppercase">Net investment</span>
              <span class="kpi-value font-extrabold">{{ calculationResult ? calculationResult.investment.totalInvestment.local : '—' }}</span>
              <span class="kpi-label text-xs" style="color: #ffffff !important; opacity: 0.8; font-weight: 500;">&nbsp;</span>
              <svg class="kpi-wave" viewBox="0 0 300 135" preserveAspectRatio="none">
                <path d="M 0 90 C 80 135, 180 120, 230 95 C 270 75, 290 65, 300 55 L 300 135 L 0 135 Z" fill="#1322b7"/>
              </svg>
            </div>
          </div>

          <!-- Card 3: Starting Salary (blurred when calculated) -->
          <div class="col-6 p-2" [class.blurred-content]="calculationResult">
            <div class="kpi-card flex flex-column justify-content-between">
              <span class="kpi-label uppercase">Starting Salary</span>
              <span class="kpi-value font-extrabold">{{ calculationResult ? calculationResult.outlook.startingSalary.local : '—' }}</span>
              <span class="kpi-label lowercase text-xs" style="color: #ffffff !important; opacity: 0.8; font-weight: 500;">per year avg</span>
              <svg class="kpi-wave" viewBox="0 0 300 135" preserveAspectRatio="none">
                <path d="M 0 90 C 80 135, 180 120, 230 95 C 270 75, 290 65, 300 55 L 300 135 L 0 135 Z" fill="#1322b7"/>
              </svg>
            </div>
          </div>

          <!-- Card 4: 10 year ROI (blurred when calculated) -->
          <div class="col-6 p-2" [class.blurred-content]="calculationResult">
            <div class="kpi-card flex flex-column justify-content-between">
              <span class="kpi-label uppercase">10 year ROI</span>
              <span class="kpi-value font-extrabold">{{ calculationResult ? (calculationResult.returns.tenYearRoiPercent + '%') : '—' }}</span>
              <span class="kpi-label text-xs" style="color: #ffffff !important; opacity: 0.8; font-weight: 500;">&nbsp;</span>
              <svg class="kpi-wave" viewBox="0 0 300 135" preserveAspectRatio="none">
                <path d="M 0 90 C 80 135, 180 120, 230 95 C 270 75, 290 65, 300 55 L 300 135 L 0 135 Z" fill="#1322b7"/>
              </svg>
            </div>
          </div>

        </div>

        <!-- Break-Even Timeline Card (blurred when calculated) -->
        <div class="results-card w-full" [class.blurred-content]="calculationResult">
          <div class="results-card-header">
            <span>Break Even Timeline</span>
          </div>
          <div class="results-card-content p-4 flex flex-column gap-3">
            <div class="flex flex-row align-items-baseline mb-3 gap-2 px-1">
              <span class="font-extrabold text-blue-900" style="font-size: 2.2rem; letter-spacing: -0.03em; line-height: 1;">
                {{ calculationResult ? calculationResult.returns.breakEvenYears : '—' }}
              </span>
              <span class="font-extrabold text-blue-900" style="font-size: 1.5rem; letter-spacing: -0.02em;" *ngIf="calculationResult">years</span>
              <span class="text-500 font-medium text-xs ml-1">from graduation to full cost recovery</span>
            </div>

            <!-- Break-Even Curve SVG Chart -->
            <div class="svg-chart-wrapper w-full relative border-none" style="height: 240px; padding: 10px 10px 25px 60px; box-sizing: border-box; margin: 0 auto; background-color: #ffffff; border-radius: 12px;">
              
              <!-- Left Y-Axis Labels -->
              <div class="chart-y-axis" style="position: absolute; left: 10px; top: 10px; bottom: 25px; width: 45px; display: flex; flex-direction: column; justify-content: space-between; align-items: flex-end; pointer-events: none; z-index: 10;">
                <ng-container *ngIf="calculationResult; else placeholderLabels">
                  <span *ngFor="let label of getBreakEvenYLabels()" style="font-size: 14px; font-weight: 600; color: #1e293b; line-height: 1;">{{ label }}</span>
                </ng-container>
                <ng-template #placeholderLabels>
                  <span style="font-size: 14px; font-weight: 600; color: #1e293b; line-height: 1;">+$120k</span>
                  <span style="font-size: 14px; font-weight: 600; color: #1e293b; line-height: 1;">+$80k</span>
                  <span style="font-size: 14px; font-weight: 600; color: #1e293b; line-height: 1;">+$40k</span>
                  <span style="font-size: 14px; font-weight: 600; color: #1e293b; line-height: 1;">$0</span>
                  <span style="font-size: 14px; font-weight: 600; color: #1e293b; line-height: 1;">-$40k</span>
                  <span style="font-size: 14px; font-weight: 600; color: #1e293b; line-height: 1;">-$80k</span>
                </ng-template>
              </div>

              <!-- Bottom X-Axis Labels -->
              <div class="chart-x-axis" style="position: absolute; left: 60px; right: 10px; bottom: 5px; height: 20px; display: flex; flex-direction: row; justify-content: space-between; pointer-events: none; z-index: 10;">
                <span style="font-size: 14px; font-weight: 500; color: #1e293b; width: 40px; text-align: center; margin-left: -20px;">start</span>
                <span style="font-size: 14px; font-weight: 500; color: #1e293b; width: 50px; text-align: center;">Year 1</span>
                <span style="font-size: 14px; font-weight: 500; color: #1e293b; width: 50px; text-align: center;">Year 2</span>
                <span style="font-size: 14px; font-weight: 500; color: #1e293b; width: 50px; text-align: center;">Year 3</span>
                <span style="font-size: 14px; font-weight: 500; color: #1e293b; width: 50px; text-align: center; margin-right: -25px;">Year 4</span>
              </div>

              <!-- SVG Chart Area -->
              <svg class="w-full h-full" viewBox="0 0 650 200" preserveAspectRatio="none">
                <!-- Grid Lines -->
                <line x1="0" y1="0" x2="650" y2="0" stroke="#f1f5f9" stroke-width="1.5" />
                <line x1="0" y1="40" x2="650" y2="40" stroke="#f1f5f9" stroke-width="1.5" />
                <line x1="0" y1="80" x2="650" y2="80" stroke="#f1f5f9" stroke-width="1.5" />
                <line x1="0" y1="120" x2="650" y2="120" stroke="#f1f5f9" stroke-width="1.5" />
                <line x1="0" y1="160" x2="650" y2="160" stroke="#f1f5f9" stroke-width="1.5" />
                <line x1="0" y1="200" x2="650" y2="200" stroke="#f1f5f9" stroke-width="1.5" />

                <!-- Break-Even curve line path (render only when result is available) -->
                <path *ngIf="calculationResult" [attr.d]="getBreakEvenPath()" fill="none" stroke="url(#lineGrad)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                
                <!-- Define Gradients -->
                <defs>
                  <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stop-color="#ff8f8f" /> <!-- Red -->
                    <stop offset="25%" stop-color="#d4aa63" /> <!-- Orange/Brown at Year 1 -->
                    <stop offset="60%" stop-color="#84e058" /> <!-- Greenish at Year 2/3 -->
                    <stop offset="100%" stop-color="#4ade80" /> <!-- Bright Green at end -->
                  </linearGradient>
                </defs>
              </svg>
            </div>

          </div>
        </div>

      </div>

      <!-- Lock Paywall Overlay (shows in the center on calculationResult) -->
      <div class="lock-overlay flex align-items-center justify-content-center" *ngIf="calculationResult">
        <div class="lock-card flex flex-column align-items-center justify-content-center p-4">
          <div class="lock-icon-badge flex align-items-center justify-content-center mb-3">
            <i class="pi pi-lock text-4xl" style="color: #0b1c77;"></i>
          </div>
          <p class="lock-message-text mb-4">
            Create a free GradPilot account to unlock your complete ROI breakdown. You got the key, just need to unlock it now!
          </p>
          <button class="btn-lock-signup mb-3" (click)="navigateToSignup()">
            Create a free Account
          </button>
          <div class="login-prompt-text">
            Already have an account? <span class="login-link-btn" (click)="navigateToSignin()">Log in</span>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>
